import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Chip, { type ChipTone } from '../../components/Chip';
import type { BomAssessment } from '../../lib/types';

const VERDICTS: Readonly<Record<string, { label: MessageDescriptor; tone: ChipTone }>> = {
  OFFEN: { label: msg`Offen`, tone: 'warn' },
  BETROFFEN: { label: msg`Betroffen`, tone: 'alert' },
  NICHT_BETROFFEN: { label: msg`Nicht betroffen`, tone: 'good' },
  BEHOBEN: { label: msg`Behoben`, tone: 'idle' },
};

/** Chip for a verdict; unknown verdicts show their API value. */
function verdictChip(verdict: string, translate: (label: MessageDescriptor) => string): { label: string; tone: ChipTone } {
  const known = VERDICTS[verdict];
  return known ? { label: translate(known.label), tone: known.tone } : { label: verdict, tone: 'neutral' };
}

interface Props {
  kevCves: readonly string[];
  assessments: readonly BomAssessment[];
  /** Text shown without findings (default "Keine Befunde"); `null` renders nothing. */
  empty?: string | null;
}

/** KEV hits and assessed CVEs of one component version, each linked to the triage. */
export default function CveFindings({ kevCves, assessments, empty }: Props): ReactNode {
  const { t, i18n } = useLingui();
  const verdicts = new Map(assessments.map((a) => [a.cve_id, a.verdict]));
  const cves = [...new Set([...kevCves, ...verdicts.keys()])].sort((a, b) => a.localeCompare(b));
  if (cves.length === 0) return empty === null ? null : <span className="muted">{empty ?? t`Keine Befunde`}</span>;
  return (
    <ul className="cve-findings">
      {cves.map((cve) => {
        const verdict = verdicts.get(cve);
        const shown = verdict ? verdictChip(verdict, i18n._.bind(i18n)) : null;
        return (
          <li key={cve}>
            <Link className="mono" to={`/triage?q=${encodeURIComponent(cve)}`} title={t`In der Triage öffnen`}
              onClick={(event) => event.stopPropagation()}>{cve}</Link>
            {kevCves.includes(cve) ? <Chip tone="alert">KEV</Chip> : null}
            {shown ? <Chip tone={shown.tone}>{shown.label}</Chip> : <Chip><Trans>Nicht bewertet</Trans></Chip>}
          </li>
        );
      })}
    </ul>
  );
}
